import React, { useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  colors,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createOtherExpenseCategory,
  CUSTOM_CATEGORY_NAME_MAX_LENGTH,
  OtherExpenseCategory,
  renameOtherExpenseCategory,
} from 'api/otherExpensesApi';

interface Props {
  open: boolean;
  onClose: () => void;
  // Renames this category; creates a new one when not given.
  editCategory?: OtherExpenseCategory | null;
}

type FormValues = { name: string };

/**
 * Creates or renames an organization's own other-expense category. Margins for
 * it are added afterwards with the margins "Add New" button, like any other
 * category.
 */
export default function AddOrEditCustomCategoryDialog({
  open,
  onClose,
  editCategory,
}: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isRename = Boolean(editCategory);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<FormValues>({ defaultValues: { name: '' } });

  useEffect(() => {
    if (open) reset({ name: editCategory?.name ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the dialog opens
  }, [open, editCategory]);

  const onSaved = (category: OtherExpenseCategory) => {
    // Margins carry the category too, so their names refresh with it.
    queryClient.invalidateQueries(['other-expense-categories']);
    queryClient.invalidateQueries(['other-expenses-margin']);
    enqueueSnackbar(
      isRename
        ? `Category renamed to "${category.name}"`
        : `Category "${category.name}" added`,
      { variant: 'success' }
    );
    onClose();
  };

  // The server explains name clashes (duplicate, archived, default name) in
  // words meant for the user, so show its message on the field.
  const onError = (error: any) => {
    const message: string | undefined = error?.response?.data?.message;
    if (error?.response?.status === 409 || error?.response?.status === 400) {
      setError('name', {
        type: 'server',
        message: message ?? 'That name cannot be used.',
      });
      return;
    }
    enqueueSnackbar(
      isRename ? 'Failed to rename category' : 'Failed to add category',
      { variant: 'error' }
    );
  };

  const createMutation = useMutation(createOtherExpenseCategory, {
    onSuccess: onSaved,
    onError,
  });
  const renameMutation = useMutation(renameOtherExpenseCategory, {
    onSuccess: onSaved,
    onError,
  });
  const isSaving = createMutation.isLoading || renameMutation.isLoading;

  const onSubmit = ({ name }: FormValues) => {
    const trimmedName = name.trim();
    if (editCategory) {
      renameMutation.mutate({ id: editCategory.id, name: trimmedName });
    } else {
      createMutation.mutate(trimmedName);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        {isRename ? 'Rename Custom Category' : 'Add Custom Category'}
      </DialogTitle>
      <Divider />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <Controller
            name="name"
            control={control}
            rules={{
              validate: (value: string) => {
                const trimmed = (value ?? '').trim();
                if (!trimmed) return 'Name is required';
                if (trimmed.length > CUSTOM_CATEGORY_NAME_MAX_LENGTH) {
                  return `Name can be at most ${CUSTOM_CATEGORY_NAME_MAX_LENGTH} characters`;
                }
                return true;
              },
            }}
            render={(field) => (
              <TextField
                {...field}
                autoFocus
                fullWidth
                required
                label="Category name"
                placeholder="e.g. Tolls"
                error={!!errors.name}
                helperText={
                  errors.name?.message ??
                  'Expenses under it are recorded like Parking: an amount, a note and optional photos.'
                }
                inputProps={{ maxLength: CUSTOM_CATEGORY_NAME_MAX_LENGTH }}
              />
            )}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ mx: 2, my: 1 }}>
          <Button
            onClick={onClose}
            disabled={isSaving}
            sx={{ color: colors.grey[700], mr: 1 }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSaving}>
            {isSaving ? 'Saving...' : isRename ? 'Rename' : 'Add Category'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
